import { useParams, Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useFetchTerminalsForUnit } from "../hooks/TerminalHooks";
import { TerminalDTO } from "../types/terminalDTO";
import { useTranslation } from 'react-i18next';
import { useState } from "react";

const TerminalList = () => {
  const { t, i18n } = useTranslation();
  const { organisationId, unitId } = useParams<{ organisationId: string, unitId: string }>();
  if (!organisationId) throw new Error("Missing organisationId from params");
  if (!unitId) throw Error("Need a unit id");
  const nav = useNavigate();
  const { data, isPending, isError } = useFetchTerminalsForUnit(organisationId, unitId);
  const [sortField, setSortField] = useState<keyof TerminalDTO>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');

  const handleSort = (field: keyof TerminalDTO) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortData = (data: TerminalDTO[]) => {
    return [...data].sort((a, b) => {
      if (sortField === 'name') {
        return sortDirection === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }

      if (sortField === 'address') {
        return sortDirection === 'asc'
          ? a.address.localeCompare(b.address)
          : b.address.localeCompare(a.address);
      }

      if (sortField === 'validFrom' || sortField === 'validTo' || sortField === 'lastModified') {
        const aValue = new Date(a[sortField]).getTime();
        const bValue = new Date(b[sortField]).getTime();
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  };

  const filteredData = data?.filter(terminal =>
    terminal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    terminal.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isError) return <h2>{t('common.error')}</h2>;
  if (isPending) return <h2>{t('common.holdOn')}</h2>;

  return (
    <div>
      <div className="row mb-2 mt-2">
        <h5 className="subtitle text-left">
          {t('terminal.title')}
        </h5>
      </div>

      <Link className="btn btn-primary mt-2 mb-3" to={`/organisation/${organisationId}/unit/${unitId}/terminal/add`}>
        {t('terminal.add')}
      </Link>

      <div className="row mb-3">
        <div className="col-md-4">
          <input
            type="text"
            className="form-control"
            placeholder={t('common.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

        <table className="table table-hover"
            style={
                {
                    "--bs-table-hover-bg": "#C9DDED"
                } as React.CSSProperties
            }
        >
        <thead>
          <tr>
            <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
              {t('common.name')} {sortField === 'name' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('address')} style={{ cursor: 'pointer' }}>
              {t('common.address')} {sortField === 'address' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('validFrom')} style={{ cursor: 'pointer' }}>
              {t('common.validFrom')} {sortField === 'validFrom' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('validTo')} style={{ cursor: 'pointer' }}>
              {t('common.validTo')} {sortField === 'validTo' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('lastModified')} style={{ cursor: 'pointer' }}>
              {t('common.lastModified')} {sortField === 'lastModified' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredData && sortData(filteredData).map((terminal: TerminalDTO) => {
            const dateOptions: Intl.DateTimeFormatOptions = {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            };

            return (
                <tr key={terminal.id} style={{ cursor: 'pointer' }} onClick={() => nav(`/organisation/${organisationId}/unit/${unitId}/terminal/${terminal.id}`)}>
                <td>{terminal.name}</td>
                <td>{terminal.address}</td>
                <td>{new Date(terminal.validFrom + 'Z').toLocaleString(i18n.language === 'fi' ? 'fi-FI' : 'en-US', dateOptions)}</td>
                <td>{new Date(terminal.validTo + 'Z').toLocaleString(i18n.language === 'fi' ? 'fi-FI' : 'en-US', dateOptions)}</td>
                <td>{new Date(terminal.lastModified + 'Z').toLocaleString(i18n.language === 'fi' ? 'fi-FI' : 'en-US', dateOptions)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TerminalList;